/**
 * echoMotion.js — движение фиброзных колец по эхо-кино через анатомический
 * М-режим: TAPSE, MAPSE, кадры конца диастолы и систолы, ЧСС по каждому
 * циклу. Чистая математика. [12.0.44]
 *
 * ПОВОД. Часть 2 из двух, о которых договорились с владельцем после 12.0.42:
 * по 4-камерной позиции без допплера и ЭКГ посчитать то, что даёт движение
 * колец.
 *
 * ПОЧЕМУ М-РЕЖИМ, А НЕ СЛЕЖЕНИЕ ЗА ТОЧКОЙ. Сначала было сделано слежение за
 * точкой по «пятнам» ткани (сопоставление блоков, как в speckle tracking). На
 * фантомах оно давало ошибку 0,1–0,2 пикселя, но на присланном клипе Mindray
 * проверочный обратный проход расходился с прямым до 18 мм: у кольца за цикл
 * меняется сама картина (створки проходят через окно, ткань уходит из
 * плоскости), и точка соскальзывает на соседнюю светлую полосу. Число при этом
 * выглядело правдоподобно — это хуже отказа. Поэтому сделано то, чем TAPSE
 * меряют на аппарате: М-режим. Врач проводит линию через кольцо к верхушке
 * (как курсор М-режима), программа собирает по ней картинку «глубина × время»
 * со всех кадров, и след кольца на ней видно глазами — врач сверяет
 * предложенный след или ставит отметки сам.
 *
 * 1. ЛИНИЯ. Первый щелчок — на кольцо, второй — к верхушке (на высоту, куда
 *    кольцо доходит в систолу, или дальше). Линия продлевается за кольцо в
 *    сторону предсердия на половину своей длины: кольцо ходит в обе стороны
 *    от места, где его отметили. Координата вдоль линии t растёт к
 *    верхушке, поэтому «+» — всегда к верхушке, как бы ни был повёрнут снимок.
 *    По ширине берётся среднее пяти соседних линий — меньше шума пятен.
 *
 * 2. СЛЕД КОЛЬЦА. Ищется граница «к верхушке темно (кровь желудочка), к
 *    основанию светло (ткань кольца)» — передний край кольца, тот, что
 *    меряют на М-режиме. Путь через все кадры выбирается целиком
 *    (динамическое программирование, алгоритм Витерби): сумма силы границы
 *    минус штраф за РЕЗКУЮ СМЕНУ СКОРОСТИ (не за скорость — кольцо в раннюю
 *    диастолу движется быстро и ровно) минус штраф за то, что со стороны
 *    верхушки светлее, чем над щелчком врача (у кольца там кровь, у перикарда
 *    и створок — ткань). На кадре, где врач провёл линию, след обязан пройти
 *    не дальше 5 пикселей от его щелчка. Без штрафа за светлую сторону
 *    фантомный след уходил с тусклого кольца на яркий неподвижный «перикард»
 *    даже со щелчком (tests/echomotion_test.mjs, раздел 3). Пробовалось и
 *    сходство картины на соседних кадрах — ни на фантоме, ни на клипе оно
 *    ничего не меняло, и его нет.
 *
 *    На присланном клипе след латерального кольца ТК дал 19–22 мм по четырём
 *    циклам, септального МК — 10–13 мм; на картинке М-режима след идёт по
 *    переднему краю кольца. Там, где картинка нечёткая, след может срезать
 *    угол — поэтому окно показывает М-режим со следом, а врач может поставить
 *    отметки КД и КС сам (ручные отметки главнее следа).
 *
 * 3. ФАЗЫ. Конец систолы — кольцо ближе всего к верхушке (пик следа), конец
 *    диастолы — дальше всего (после сокращения предсердий). Пик признаётся,
 *    если выступает над соседними впадинами не меньше чем на 40% размаха:
 *    бугорок диастазиса пиком не считается, а неровные циклы не мешают —
 *    период заранее не нужен. Пик и впадина на первом или последнем кадре —
 *    обрез клипа, они отбрасываются: считаются только целые циклы.
 *
 * 4. ЧИСЛА. Экскурсия цикла — путь кольца вдоль линии от конца диастолы до
 *    следующего конца систолы, мм. ЧСС — по промежутку между соседними
 *    концами диастолы (у кольца это почти зубец R). Время кадра — из файла; у
 *    AVI оно из заголовка видео, и окно просит один раз сверить ЧСС с пульсом.
 *    Разброс промежутков показывается как есть; ритм по нему не ставится.
 *
 * Файл без импортов — правило проверяется тестом.
 */

// ══════════════════════════════════════════════════════════════
//  Пороги
// ══════════════════════════════════════════════════════════════

// ПО ПАМЯТИ, как и ПОРОГИ в echoSimpson.js. Сверьте с действующими.
export const ПОРОГИ_ДВИЖЕНИЯ = Object.freeze({
  tapse: { ниже: 17, ед: 'мм', источник: 'ASE/EACVI 2015' },
});

export const ЛИНИИ = Object.freeze({
  тк:  { имя: 'Латеральное кольцо ТК', показатель: 'TAPSE',       порог: 'tapse' },
  мкс: { имя: 'Септальное кольцо МК',  показатель: 'MAPSE септ.', порог: null },
  мкл: { имя: 'Латеральное кольцо МК', показатель: 'MAPSE лат.',  порог: null },
});

// ══════════════════════════════════════════════════════════════
//  1. Линия и картинка М-режима
// ══════════════════════════════════════════════════════════════

/**
 * Линия М-режима по двум щелчкам (пиксели снимка).
 * @returns {{ок:boolean, причина?:string, начало?:number[], направление?:number[], длина?:number, якорь?:number}}
 *   точка с координатой t — начало + направление·t, t = 0…длина; якорь — t щелчка на кольце
 */
export function линияМ(кольцо, верхушка) {
  if (![кольцо, верхушка].every((т) => Array.isArray(т) && Number.isFinite(т[0]) && Number.isFinite(т[1]))) {
    return { ок: false, причина: 'нужны два щелчка: на кольцо и к верхушке' };
  }
  const dx = верхушка[0] - кольцо[0], dy = верхушка[1] - кольцо[1];
  const L = Math.hypot(dx, dy);
  if (L < 20) return { ок: false, причина: 'линия короче 20 пикселей — второй щелчок дальше к верхушке, на высоту, куда кольцо доходит в систолу' };
  const d = [dx / L, dy / L];
  const якорь = Math.round(L / 2);
  const начало = [кольцо[0] - d[0] * якорь, кольцо[1] - d[1] * якорь];
  return { ок: true, начало, направление: d, длина: якорь + Math.round(L), якорь };
}

/** Точка линии с координатой t, пиксели снимка. */
export function точкаЛинии(линия, t) {
  return [линия.начало[0] + линия.направление[0] * t, линия.начало[1] + линия.направление[1] * t];
}

/**
 * Яркость вдоль линии на одном кадре — один столбец М-режима.
 * @param {ArrayLike<number>} пиксели как отдаёт снимок: серый (комп = 1) или RGB/RGBA
 * @param {number} комп 1, 3 или 4
 * @param {number} [ширина] сколько соседних линий с каждой стороны усреднять
 * @returns {Float64Array} длина + 1 значений, индекс = t
 */
export function столбецМ(пиксели, w, h, комп, линия, ширина = 2) {
  const [ox, oy] = линия.начало, [dx, dy] = линия.направление;
  const px = -dy, py = dx;
  const каналов = Math.min(комп, 3);
  const яр = (x, y) => {
    let v = 0;
    const адр = (y * w + x) * комп;
    for (let c = 0; c < каналов; c++) v += пиксели[адр + c];
    return v / каналов;
  };
  const в = (x, y) => {
    if (x < 0) x = 0; else if (x > w - 1) x = w - 1;
    if (y < 0) y = 0; else if (y > h - 1) y = h - 1;
    const x0 = Math.floor(x), y0 = Math.floor(y);
    const x1 = x0 + 1 < w ? x0 + 1 : x0, y1 = y0 + 1 < h ? y0 + 1 : y0;
    const fx = x - x0, fy = y - y0;
    return (яр(x0, y0) * (1 - fx) + яр(x1, y0) * fx) * (1 - fy) + (яр(x0, y1) * (1 - fx) + яр(x1, y1) * fx) * fy;
  };
  const out = new Float64Array(линия.длина + 1);
  for (let t = 0; t <= линия.длина; t++) {
    let s = 0;
    for (let q = -ширина; q <= ширина; q++) s += в(ox + dx * t + px * q, oy + dy * t + py * q);
    out[t] = s / (2 * ширина + 1);
  }
  return out;
}

// ══════════════════════════════════════════════════════════════
//  2. След кольца
// ══════════════════════════════════════════════════════════════

/**
 * Сила границы «к верхушке темно, к основанию светло» в каждой точке
 * столбца: среднее под (к основанию, 3 пикселя, включая саму точку) минус
 * среднее над (к верхушке, 6 пикселей). Нормирована на 95-й перцентиль
 * положительных значений по всему клипу, обрезана в [−1; 1,5].
 */
function силаГраницы(М, { под = 3, над = 6 } = {}) {
  const N = М[0].length;
  const G = М.map((ст) => {
    const g = new Float64Array(N).fill(-5);
    for (let t = под - 1; t < N - над; t++) {
      let a = 0, b = 0;
      for (let q = 0; q < под; q++) a += ст[t - q];
      for (let q = 1; q <= над; q++) b += ст[t + q];
      g[t] = a / под - b / над;
    }
    return g;
  });
  const пол = [];
  for (const g of G) for (let t = 0; t < N; t++) if (g[t] > 0) пол.push(g[t]);
  пол.sort((a, b) => a - b);
  const шк = пол.length ? пол[Math.floor(пол.length * 0.95)] : 0;
  if (!(шк > 1)) return null;          // перепадов нет или они на уровне шума
  for (const g of G) for (let t = 0; t < N; t++) g[t] = g[t] <= -5 ? -5 : Math.max(-1, Math.min(1.5, g[t] / шк));
  return G;
}

/**
 * След кольца по картинке М-режима.
 *
 * @param {Float64Array[]} М столбцы по кадрам (столбецМ)
 * @param {object} о
 * @param {number} о.кадр номер кадра (с нуля), на котором провели линию
 * @param {number} о.якорь t щелчка на кольце
 * @param {number} [о.ммНаПиксель] — только чтобы задать предел скорости в мм
 * @returns {{ок:boolean, причина?:string, след?:number[], сила?:number[], слабые?:number[]}}
 *   след — t по кадрам; слабые — номера кадров (с единицы), где границы почти нет
 */
export function следКольца(М, { кадр = 0, якорь, ммНаПиксель = null, снап = 5, μ = 0.08, λ = 0.01, κ = 4 } = {}) {
  const n = М?.length || 0;
  if (n < 5) return { ок: false, причина: 'в клипе меньше пяти кадров' };
  const N = М[0].length;
  if (!(N > 12)) return { ок: false, причина: 'линия слишком короткая' };
  if (!(кадр >= 0 && кадр < n) || !(якорь >= 0 && якорь < N)) return { ок: false, причина: 'щелчок на кольце вне линии' };
  const G0 = силаГраницы(М);
  if (!G0) return { ок: false, причина: 'на линии нет перехода «темно — светло»: линия не пересекает кольцо' };
  // Со стороны желудочка у кольца — кровь, она тёмная весь цикл. У других
  // границ того же знака на линии (перикард, створка, стенка) со стороны
  // верхушки — ткань, она светлее. Поэтому штраф за то, что «над» границей
  // светлее, чем над щелчком врача: с запасом в четверть перепада под
  // щелчком, дальше — растёт. Без этого слагаемого след уходил с тусклого
  // кольца на яркий перикард даже со щелчком (tests/echomotion_test.mjs,
  // раздел 3).
  const над = (k, t) => { let b = 0; for (let q = 1; q <= 6; q++) b += М[k][Math.min(N - 1, t + q)]; return b / 6; };
  let tя = якорь;
  for (let t = Math.max(0, якорь - снап); t <= Math.min(N - 1, якорь + снап); t++) if (G0[кадр][t] > G0[кадр][tя]) tя = t;
  const кровьЯ = над(кадр, tя);
  const перепадЯ = Math.max(1, (М[кадр][tя] + М[кадр][Math.max(0, tя - 1)] + М[кадр][Math.max(0, tя - 2)]) / 3 - кровьЯ);
  const G = G0.map((g, k) => g.map((v, t) => (v <= -5 ? v : v - κ * Math.max(0, (над(k, t) - кровьЯ) / перепадЯ - 0.25))));
  // Предел скорости: 8 мм за кадр (≈ 20 см/с при 25 кадрах/с), без масштаба — 16 пикселей.
  const V = ммНаПиксель > 0 ? Math.max(8, Math.min(40, Math.ceil(8 / ммНаПиксель))) : 16;
  const NV = 2 * V + 1;
  const можно = (k, t) => k !== кадр || Math.abs(t - якорь) <= снап;
  let пред = new Float64Array(N * NV).fill(-Infinity);
  for (let t = 0; t < N; t++) if (можно(0, t)) пред[t * NV + V] = G[0][t];
  const откуда = [null];
  for (let k = 1; k < n; k++) {
    const тек = new Float64Array(N * NV).fill(-Infinity);
    const о = new Int32Array(N * NV).fill(-1);
    for (let t = 0; t < N; t++) {
      if (!можно(k, t)) continue;
      for (let v = -V; v <= V; v++) {
        const tp = t - v;
        if (tp < 0 || tp >= N) continue;
        let лучш = -Infinity, bv = 0;
        const база = tp * NV + V;
        for (let vp = -V; vp <= V; vp++) {
          const x = пред[база + vp];
          if (x === -Infinity) continue;
          const s = x - μ * Math.abs(v - vp);
          if (s > лучш) { лучш = s; bv = vp; }
        }
        if (лучш === -Infinity) continue;
        тек[t * NV + v + V] = лучш + G[k][t] - λ * Math.abs(v);
        о[t * NV + v + V] = база + bv;
      }
    }
    откуда.push(о);
    пред = тек;
  }
  let i = 0;
  for (let j = 1; j < пред.length; j++) if (пред[j] > пред[i]) i = j;
  if (пред[i] === -Infinity) return { ок: false, причина: 'след не найден' };
  const след = new Array(n);
  for (let k = n - 1; k >= 0; k--) { след[k] = Math.floor(i / NV); if (k) i = откуда[k][i]; }
  const сила = след.map((t, k) => G0[k][t]);
  // Доли пикселя — вершиной параболы по силе границы в трёх соседних точках:
  // иначе след ступенчатый, и конец диастолы (острое дно) гуляет на кадр.
  for (let k = 0; k < n; k++) {
    const t = след[k], g = G0[k];
    if (t < 1 || t > N - 2 || g[t - 1] <= -5 || g[t + 1] <= -5) continue;
    const зн = g[t - 1] - 2 * g[t] + g[t + 1];
    if (!(зн < 0)) continue;
    const d = 0.5 * (g[t - 1] - g[t + 1]) / зн;
    if (Math.abs(d) <= 0.5) след[k] = t + d;
  }
  const слабые = [];
  сила.forEach((с, k) => { if (с < 0.15) слабые.push(k + 1); });
  return { ок: true, след, сила, слабые };
}

// ══════════════════════════════════════════════════════════════
//  3. Фазы
// ══════════════════════════════════════════════════════════════

function безДрейфа(с) {
  const n = с.length;
  let sk = 0, sv = 0, skk = 0, skv = 0;
  for (let k = 0; k < n; k++) { sk += k; sv += с[k]; skk += k * k; skv += k * с[k]; }
  const b = (n * skv - sk * sv) / Math.max(n * skk - sk * sk, 1e-12), a = (sv - b * sk) / n;
  return с.map((v, k) => v - a - b * k);
}

/** Выступ пика: насколько он выше более высокого из двух «сёдел» по бокам. */
function выступ(с, k) {
  const v = с[k];
  let л = v, п = v;
  for (let j = k - 1; j >= 0 && с[j] <= v; j--) if (с[j] < л) л = с[j];
  for (let j = k + 1; j < с.length && с[j] <= v; j++) if (с[j] < п) п = с[j];
  return v - Math.max(л, п);
}

/**
 * Концы систолы (пики) и диастолы (впадины) по следу.
 * @param {number[]} сигнал «+» — к верхушке, в пикселях
 * @returns {{ок:boolean, причина?:string, кс:number[], кд:number[]}} номера кадров с нуля
 */
export function фазы(сигнал, { доля = 0.4, минРазмах = 2 } = {}) {
  const n = сигнал?.length || 0;
  if (n < 5) return { ок: false, причина: 'слишком короткий клип', кс: [], кд: [] };
  const с = безДрейфа(сигнал);
  const мин = Math.min(...с), макс = Math.max(...с), размах = макс - мин;
  // Меньше 2 пикселей (≈ 0,7 мм на Mindray) — это дрожь следа, а не сокращение.
  if (!(размах >= минРазмах)) return { ок: false, причина: 'кольцо на линии не двигается (размах следа меньше 2 пикселей)', кс: [], кд: [] };
  // След целочисленный, поэтому вершины и дно часто плоские на 2–4 кадра:
  // берётся середина плоского участка, а не первый его кадр.
  const середина = (k) => { let j = k; while (j + 1 < n && с[j + 1] === с[k]) j++; return Math.round((k + j) / 2); };
  const кс = [];
  for (let k = 1; k < n - 1; k++) {
    if (!(с[k] > с[k - 1] && с[k] >= с[k + 1])) continue;
    if (выступ(с, k) < доля * размах) continue;
    let j = k; while (j + 1 < n && с[j + 1] === с[k]) j++;
    if (j === n - 1) continue;          // плоская вершина до конца клипа — обрез, не пик
    кс.push(середина(k));
  }
  if (!кс.length) return { ок: false, причина: 'в клипе нет целого сокращения: нет пика, от которого кольцо ушло бы вниз и вернулось', кс: [], кд: [] };
  const нижайшая = (от, до) => {
    let л = -1;
    for (let k = от; k < до; k++) if (л < 0 || с[k] < с[л]) л = k;
    if (л < 0) return л;
    let j = л; while (j + 1 < до && с[j + 1] === с[л]) j++;
    return л === 0 ? 0 : Math.round((л + j) / 2);   // дно с первого кадра — обрез, его середина ни к чему
  };
  const кд = [];
  {
    const k = нижайшая(0, кс[0]);
    let слеваВыше = 0;
    for (let j = 0; j < k; j++) слеваВыше = Math.max(слеваВыше, с[j] - с[k]);
    if (k > 0 && слеваВыше >= 0.1 * размах && с[кс[0]] - с[k] >= доля * размах) кд.push(k);
  }
  for (let i = 1; i < кс.length; i++) кд.push(нижайшая(кс[i - 1] + 1, кс[i]));
  {
    const k = нижайшая(кс[кс.length - 1] + 1, n);
    let справаВыше = 0;
    for (let j = k + 1; j < n; j++) справаВыше = Math.max(справаВыше, с[j] - с[k]);
    if (k > 0 && k < n - 1 && справаВыше >= 0.25 * размах && с[кс[кс.length - 1]] - с[k] >= доля * размах) кд.push(k);
  }
  кд.sort((a, b) => a - b);
  return { ок: true, кс, кд };
}

// ══════════════════════════════════════════════════════════════
//  4. Циклы: экскурсия и ЧСС
// ══════════════════════════════════════════════════════════════

const среднее = (м) => м.reduce((a, b) => a + b, 0) / м.length;

/** Разброс промежутков в кадрах (коэффициент вариации) — не зависит от времени кадра. */
function разбросКадров(rr) {
  const м = rr.map((р) => р.кадров), с = среднее(м);
  return Math.sqrt(м.reduce((a, b) => a + (b - с) ** 2, 0) / (м.length - 1)) / с;
}

/**
 * Промежутки между соседними концами диастолы.
 * @returns {{rr:object[], чсс:number|null, разбросRR:number|null}}
 */
export function ритм(кд, мсНаКадр = null) {
  const rr = [];
  const к = [...кд].sort((a, b) => a - b);
  for (let i = 1; i < к.length; i++) {
    const кадров = к[i] - к[i - 1];
    const мс = мсНаКадр > 0 ? кадров * мсНаКадр : null;
    rr.push({ от: к[i - 1], до: к[i], кадров, мс, чсс: мс ? 60000 / мс : null });
  }
  let чсс = null, разбросRR = null;
  if (rr.length && мсНаКадр > 0) {
    const м = rr.map((р) => р.мс);
    const с = среднее(м);
    чсс = 60000 / с;
    if (м.length >= 2) разбросRR = Math.sqrt(м.reduce((a, b) => a + (b - с) ** 2, 0) / (м.length - 1)) / с;
  }
  return { rr, чсс, разбросRR };
}

/**
 * Экскурсия по циклам по следу: от каждого КД до следующего КС (раньше
 * следующего КД).
 */
export function экскурсииПоСледу(след, ф, ммНаПиксель = null) {
  const мм = ммНаПиксель > 0 ? ммНаПиксель : null;
  const итог = [];
  for (const д of ф.кд) {
    const с = ф.кс.find((k) => k > д);
    if (с == null) continue;
    const следКД = ф.кд.find((k) => k > д);
    if (следКД != null && с > следКД) continue;
    const пикс = след[с] - след[д];
    итог.push({ кд: д, кс: с, пикс, мм: мм ? пикс * мм : null });
  }
  return итог;
}

/**
 * Ручные отметки врача на картинке М-режима: {кадр, t, вид: 'кд'|'кс'}.
 * Каждый КД сцепляется с ближайшим следующим КС (раньше следующего КД).
 */
export function экскурсииПоОтметкам(отметки, ммНаПиксель = null) {
  const мм = ммНаПиксель > 0 ? ммНаПиксель : null;
  const о = [...(отметки || [])].filter((x) => Number.isFinite(x?.кадр) && Number.isFinite(x?.t)).sort((a, b) => a.кадр - b.кадр);
  const кд = о.filter((x) => x.вид === 'кд'), кс = о.filter((x) => x.вид === 'кс');
  const итог = [];
  for (const д of кд) {
    const с = кс.find((x) => x.кадр > д.кадр);
    if (!с) continue;
    const следКД = кд.find((x) => x.кадр > д.кадр);
    if (следКД && с.кадр > следКД.кадр) continue;
    const пикс = с.t - д.t;
    итог.push({ кд: д.кадр, кс: с.кадр, пикс, мм: мм ? пикс * мм : null });
  }
  return { экскурсии: итог, кд: кд.map((x) => x.кадр), кс: кс.map((x) => x.кадр) };
}

/**
 * Всё по одной линии: след (или ручные отметки — они главнее), фазы, экскурсия.
 * @param {{след?:object, отметки?:object[]}} л
 */
export function разборЛинии(л, { ммНаПиксель = null, мсНаКадр = null } = {}) {
  const пусто = { экскурсии: [], кд: [], кс: [], rr: [], чсс: null, разбросRR: null };
  if ((л?.отметки || []).length > 0) {
    const р = экскурсииПоОтметкам(л.отметки, ммНаПиксель);
    return { ок: р.экскурсии.length > 0, причина: р.экскурсии.length ? '' : 'нужна пара отметок: КД, затем КС того же цикла',
      откуда: 'отметки врача', экскурсии: р.экскурсии, кд: р.кд, кс: р.кс, ...ритм(р.кд, мсНаКадр) };
  }
  if (!л?.след?.ок) return { ок: false, причина: л?.след?.причина || 'следа нет', откуда: 'след', ...пусто };
  const ф = фазы(л.след.след);
  if (!ф.ок) return { ок: false, причина: ф.причина, откуда: 'след', ...пусто };
  const экскурсии = экскурсииПоСледу(л.след.след, ф, ммНаПиксель);
  return { ок: экскурсии.length > 0, причина: экскурсии.length ? '' : 'нет ни одного целого цикла «КД → КС»',
    откуда: 'след', экскурсии, кд: ф.кд, кс: ф.кс, ...ритм(ф.кд, мсНаКадр) };
}

// ══════════════════════════════════════════════════════════════
//  5. Сводка для окна и отчёта
// ══════════════════════════════════════════════════════════════

/**
 * @param {{тк?:object, мкс?:object, мкл?:object}} линии по ключам ЛИНИИ: {след, отметки}
 * @returns {{строки:object[], фазы:object|null, фазыПо:string, чсс, rr, разбросRR, оговорки:string[], разборы:object}}
 */
export function итогДвижения(линии, { ммНаПиксель = null, мсНаКадр = null } = {}) {
  const разборы = {};
  for (const к of Object.keys(ЛИНИИ)) if (линии?.[к]) разборы[к] = разборЛинии(линии[к], { ммНаПиксель, мсНаКадр });
  const строки = [], оговорки = [];
  const мм = ммНаПиксель > 0;
  const запятая = (v) => v.toFixed(1).replace('.', ',');
  for (const [к, р] of Object.entries(разборы)) {
    const т = ЛИНИИ[к];
    if (!р.ок) { оговорки.push(`${т.имя}: ${р.причина}`); continue; }
    const знач = р.экскурсии.map((э) => (мм ? э.мм : э.пикс));
    const ср = среднее(знач);
    let вне = null;
    if (т.порог && мм) {
      const п = ПОРОГИ_ДВИЖЕНИЯ[т.порог];
      if (п.ниже != null && ср < п.ниже) вне = `ниже ${п.ниже} ${п.ед} (${п.источник})`;
    }
    if (знач.length >= 2 && (Math.max(...знач) - Math.min(...знач)) > 0.25 * Math.abs(ср)) {
      оговорки.push(`${т.имя}: циклы расходятся больше чем на четверть (${знач.map(запятая).join('; ')}) — проверьте след на картинке М-режима.`);
    }
    if (ср <= 0) оговорки.push(`${т.имя}: экскурсия получилась отрицательной — линия проведена от верхушки к кольцу? Первый щелчок — на кольцо, второй — к верхушке.`);
    строки.push({ ключ: к, имя: т.показатель, значение: ср, ед: мм ? 'мм' : 'пикс', знаков: мм ? 1 : 0,
      поЦиклам: знач, циклов: знач.length, откуда: р.откуда, вне });
  }
  const мкс = строки.find((с) => с.ключ === 'мкс'), мкл = строки.find((с) => с.ключ === 'мкл');
  if (мкс && мкл) {
    строки.push({ ключ: 'мк', имя: 'MAPSE среднее', значение: (мкс.значение + мкл.значение) / 2, ед: мкс.ед, знаков: мкс.знаков,
      поЦиклам: [], циклов: Math.min(мкс.циклов, мкл.циклов), откуда: '', вне: null });
  }
  // Кадры КД/КС и ЧСС — по самому ровному следу: сердце одно, и если у одной
  // линии промежутки между циклами скачут, а у другой нет, скачет след, а не
  // ритм (так на присланном клипе: след латерального МК в одном цикле
  // соскочил). Ручные отметки — главнее любого следа. При равенстве — МК.
  const годные = ['мкл', 'мкс', 'тк'].filter((к) => разборы[к]?.ок);
  // Отметки врача главнее, если по ним есть ЧСС (хотя бы два промежутка);
  // одна пара отметок — для экскурсии, фазы и ЧСС тогда берутся по следу.
  const оценка = (к) => {
    const р = разборы[к], ровность = р.rr.length >= 2 ? разбросКадров(р.rr) : null;
    if (р.откуда === 'отметки врача') return ровность != null ? -1 : 2;
    return ровность != null ? ровность : 0.5;
  };
  const главная = годные.length ? годные.reduce((a, b) => (оценка(b) < оценка(a) - 1e-9 ? b : a)) : null;
  const гр = главная ? разборы[главная] : null;
  // Линии про одно сердце: концы диастолы у них должны совпасть (±2 кадра).
  for (const к of годные) {
    if (к === главная || разборы[к].откуда === 'отметки врача') continue;
    const чужие = разборы[к].кд.filter((f) => f > гр.кд[0] - 3 && f < гр.кд[гр.кд.length - 1] + 3);
    const врозь = чужие.filter((f) => !гр.кд.some((g) => Math.abs(g - f) <= 2));
    if (врозь.length) оговорки.push(`${ЛИНИИ[к].имя}: концы диастолы (кадры ${врозь.map((f) => f + 1).join(', ')}) не совпали с найденными по линии «${ЛИНИИ[главная].имя}» — след одной из линий ошибся, проверьте картинку.`);
  }
  if (!мм && строки.length) оговорки.push('Масштаба нет — экскурсия в пикселях. Введите масштаб вверху окна.');
  if (гр && !(мсНаКадр > 0)) оговорки.push('Времени кадра в файле нет — ЧСС не посчитать.');
  else if (гр && гр.rr.length === 0) оговорки.push('Меньше двух концов диастолы в клипе — ЧСС не посчитать.');
  return {
    строки, разборы,
    фазы: гр ? { кд: гр.кд, кс: гр.кс } : null,
    фазыПо: главная ? ЛИНИИ[главная].имя + (гр.откуда === 'отметки врача' ? ' (отметки врача)' : '') : '',
    чсс: гр?.чсс ?? null, rr: гр?.rr ?? [], разбросRR: гр?.разбросRR ?? null,
    оговорки,
  };
}

export function оговоркиДвижения() {
  return [
    'Экскурсия меряется по анатомическому М-режиму, собранному из кадров B-режима по вашей линии: путь переднего края кольца вдоль линии от конца диастолы до конца систолы. Линия — как курсор М-режима: через кольцо, по направлению его движения. Косая линия занижает число.',
    'Красный след — предложение программы. Проверьте на картинке, что он идёт по переднему краю кольца; если нет — поставьте отметки КД и КС сами, они главнее следа.',
    'Кадры конца диастолы и систолы взяты по движению кольца (ниже всего — КД, ближе всего к верхушке — КС), не по ЭКГ: это подсказка, а не отметка клапанов.',
    'ЧСС — по времени кадра из файла. У видео AVI это частота записи ролика: сверьте ЧСС с пульсом или ЭКГ хотя бы один раз для этого аппарата. Разброс промежутков — не диагноз ритма.',
    `Порог TAPSE < ${ПОРОГИ_ДВИЖЕНИЯ.tapse.ниже} мм (${ПОРОГИ_ДВИЖЕНИЯ.tapse.источник}) перенесён по памяти; для MAPSE общего порога в этих рекомендациях нет, поэтому MAPSE не выделяется.`,
  ];
}

export default { ПОРОГИ_ДВИЖЕНИЯ, ЛИНИИ, линияМ, точкаЛинии, столбецМ, следКольца, фазы, ритм,
  экскурсииПоСледу, экскурсииПоОтметкам, разборЛинии, итогДвижения, оговоркиДвижения };
